/**
 * Base API client for HTTP requests
 * Responsible for basic HTTP operations
 */
class ApiClient {
  /**
   * Core request method used by all HTTP methods
   * @private
   * @param {string} url - URL of the request
   * @param {Object} options - Fetch options
   * @param {boolean} expectJson - Whether to expect JSON response
   * @returns {Promise} - Promise resolving response or JSON data
   */
  static async request(url, options = {}, expectJson = true) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    const defaultHeaders = {
      'X-CSRF-Token': csrfToken
    }

    const mergedOptions = {
      ...options,
      credentials: 'same-origin',
      headers: {
        ...defaultHeaders,
        ...options.headers
      }
    }

    const response = await fetch(url, mergedOptions)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const errorMessage = this.formatErrorMessage(errorData)
      throw new Error(errorMessage)
    }

    return expectJson ? response.json() : response
  }

  /**
   * Execute a GET request
   * @param {string} url - URL of the request
   * @returns {Promise} - Promise resolving JSON data
   */
  static get(url) {
    return this.request(url, {
      method: 'GET',
      cache: 'no-store',
      headers: {
        Accept: 'application/json'
      }
    })
  }

  /**
   * Execute a POST request
   * @param {string} url - URL of the request
   * @param {Object} data - Data to send
   * @returns {Promise} - Promise resolving JSON data
   */
  static post(url, data) {
    return this.request(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(data)
    })
  }

  /**
   * Execute a PUT request
   * @param {string} url - URL of the request
   * @param {Object} data - Data to send
   * @returns {Promise} - Promise resolving JSON data
   */
  static put(url, data) {
    return this.request(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(data)
    })
  }

  /**
   * Execute a PATCH request
   * @param {string} url - URL of the request
   * @param {Object} data - Data to send
   * @returns {Promise} - Promise resolving JSON data
   */
  static patch(url, data) {
    return this.request(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(data)
    })
  }

  /**
   * Execute a DELETE request
   * @param {string} url - URL of the request
   * @returns {Promise} - Promise resolving response
   */
  static delete(url) {
    return this.request(url, {
      method: 'DELETE'
    }, false)
  }

  /**
   * Execute a POST request with FormData (for file uploads)
   * @param {string} url - URL of the request
   * @param {FormData} formData - FormData to send
   * @returns {Promise} - Promise resolving JSON data
   */
  static postFormData(url, formData) {
    return this.request(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json'
      },
      body: formData
    })
  }

  /**
   * Execute a PATCH request with FormData (for file uploads)
   * @param {string} url - URL of the request
   * @param {FormData} formData - FormData to send
   * @returns {Promise} - Promise resolving JSON data
   */
  static patchFormData(url, formData) {
    return this.request(url, {
      method: 'PATCH',
      headers: {
        Accept: 'application/json'
      },
      body: formData
    })
  }

  /**
   * Format the error message
   * @private
   * @param {Object} errorData - Error data
   * @returns {string} - Formatted error message
   */
  static formatErrorMessage(errorData) {
    if (!errorData)
      return 'Une erreur est survenue'

    const error = errorData.message || errorData.error || this.collectFieldErrors(errorData.errors)
    if (!error)
      return 'Une erreur est survenue'

    if (typeof error === 'string')
      return error
    return error.join(', ')
  }

  /**
   * Flatten Rails validation failures rendered as { errors: { field: [messages] } }
   */
  static collectFieldErrors(errors) {
    if (!errors)
      return null
    return Array.isArray(errors) ? errors : Object.values(errors).flat()
  }
}

export default ApiClient